import { getFeed } from "@yoast/helpers";
import getEnv from "../../functions/getEnv";
import { decodeHtmlEntities } from "../../functions/stringHelpers";

export const GET_FEED_REQUEST = "GET_FEED_REQUEST";
export const GET_FEED_SUCCESS = "GET_FEED_SUCCESS";
export const GET_FEED_FAILURE = "GET_FEED_FAILURE";

export const GET_WEBINAR_REQUEST = "GET_WEBINAR_REQUEST";
export const GET_WEBINAR_SUCCESS = "GET_WEBINAR_SUCCESS";
export const GET_WEBINAR_FAILURE = "GET_WEBINAR_FAILURE";


/**
 * An action creator for the server request action.
 *
 * @returns {Object} A server request action.
 */
export function getFeedRequest() {
	return {
		type: GET_FEED_REQUEST,
	};
}

/**
 * An action creator for the get feed success action.
 *
 * @param {Object} blogFeed The feed that was retrieved.
 *
 * @returns {Object} A get feed success action.
 */
export function getFeedSuccess( blogFeed ) {
	return {
		type: GET_FEED_SUCCESS,
		blogFeed,
	};
}

/**
 * An action creator for the get feed failure action.
 *
 * @param {string} error The error.
 *
 * @returns {Object} A get feed failure action.
 */
export function getFeedFailure( error ) {
	return {
		type: GET_FEED_FAILURE,
		blogFeedError: error,
	};
}

/**
 * An action creator for the retrieve feed action.
 *
 * @param {number} retrieveNumber The number of blog-posts to retrieve.
 *
 * @returns {Object} A retrieve feed action.
 */
export function retrieveFeed( retrieveNumber ) {
	return ( dispatch ) => {
		dispatch( getFeedRequest() );
		return getFeed( "https://yoast.com/feed/widget/", retrieveNumber )
			.then( blogFeed => dispatch( getFeedSuccess( blogFeed ) ) )
			.catch( error => dispatch( getFeedFailure( error ) ) );
	};
}

/**
 * An action creator that retrieves the latest dashboard blog posts (with images) from yoast.com.
 *
 * Uses the JSON endpoint rather than the RSS widget feed because the latter does not
 * expose featured images.
 *
 * @returns {Function} A thunk that fetches the dashboard posts.
 */
export function retrieveDashboardPosts() {
	return ( dispatch ) => {
		dispatch( getFeedRequest() );
		const url = getEnv( "SHOP_URL", "https://yoast.test" ) + "/wp-json/yoast/v1/dashboard-posts";
		return fetch( url )
			.then( response => {
				if ( ! response.ok ) {
					throw new Error( `Failed to retrieve dashboard posts: ${ response.status }` );
				}
				return response.json();
			} )
			// yoast.com returns the titles and descriptions with their WordPress entities intact.
			.then( data => dispatch( getFeedSuccess( {
				items: ( data.items || [] ).map( item => ( {
					...item,
					title: decodeHtmlEntities( item.title ),
					description: decodeHtmlEntities( item.description ),
				} ) ),
			} ) ) )
			.catch( error => dispatch( getFeedFailure( error ) ) );
	};
}

/**
 * An action creator for the webinar request action.
 *
 * @returns {Object} A webinar request action.
 */
export function getWebinarRequest() {
	return {
		type: GET_WEBINAR_REQUEST,
	};
}

/**
 * An action creator for the get webinar success action.
 *
 * @param {Object} webinar The upcoming webinar that was retrieved.
 *
 * @returns {Object} A get webinar success action.
 */
export function getWebinarSuccess( webinar ) {
	return {
		type: GET_WEBINAR_SUCCESS,
		webinar,
	};
}

/**
 * An action creator for the get webinar failure action.
 *
 * @param {string} error The error.
 *
 * @returns {Object} A get webinar failure action.
 */
export function getWebinarFailure( error ) {
	return {
		type: GET_WEBINAR_FAILURE,
		webinarError: error,
	};
}

/**
 * An action creator for retrieving the next upcoming webinar from yoast.com.
 *
 * @returns {Function} A thunk that fetches the upcoming webinar.
 */
export function retrieveWebinar() {
	return ( dispatch ) => {
		dispatch( getWebinarRequest() );
		const url = getEnv( "SHOP_URL", "https://yoast.test" ) + "/wp-json/yoast/v1/upcoming-webinar";
		return fetch( url )
			.then( response => {
				if ( ! response.ok ) {
					throw new Error( `Failed to retrieve webinar: ${ response.status }` );
				}
				return response.json();
			} )
			// Map the WordPress (snake_case) payload to the camelCase shape the client uses,
			// decoding the text fields that WordPress hands us with their entities intact.
			.then( webinar => dispatch( getWebinarSuccess( {
				found: webinar.found,
				title: decodeHtmlEntities( webinar.title ),
				url: webinar.url,
				image: webinar.image,
				description: decodeHtmlEntities( webinar.description ),
				formattedDate: webinar.formatted_date,
				subheading: decodeHtmlEntities( webinar.subheading ),
				archiveUrl: webinar.archive_url,
				registrationLink: {
					url: webinar.registration_link ? webinar.registration_link.url : "",
					title: webinar.registration_link ? decodeHtmlEntities( webinar.registration_link.title ) : "",
				},
			} ) ) )
			.catch( error => dispatch( getWebinarFailure( error ) ) );
	};
}
